const angular = require('angular');
const BigNumber = require('bignumber.js');
const DateFormat = require('@/lib/Dates/DateFormat');
const EventNames = require('@/lib/Events/EventNames');
const moment = require('moment');
const { container } = require('@/container');
const { setLoggedInBodyClass } = require('@/lib/Dom/domFunctions');

/**
 * app.run logic common to all components.
 * This is designed to be called within a call to app.run()
 *
 * @param $injector
 * @param $rootScope
 * @param $timeout
 * @param $window
 * @param {ApiClient} apiClient
 * @param {AppConfig} appConfig
 * @param {ConnectivityChecker} connectivityChecker
 * @param {CurrentDevice} currentDevice
 * @param {DeviceLoginListener} deviceLoginListener
 * @param {DeviceManager} deviceManager
 * @param {UserService} userService
 * @param {TimeSyncer} timeSyncer
 */
let runAppCommon = function runAppCommon(
    $injector,
    $rootScope,
    $timeout,
    $window,
    apiClient,
    appConfig,
    connectivityChecker,
    currentDevice,
    deviceLoginListener,
    deviceManager,
    userService,
    timeSyncer
) {
    // Angular's $injector is made available to Vue and our own DI container via the global $injector variable.
    window.$injector = $injector;

    // Make the container available on the window object for easy access in the browser console.
    window.container = container;

    console.log(`ProPoint Version ${appConfig.eposCommit} ${appConfig.eposBranch} ${appConfig.eposVersion}`);

    // The maximum number of decimal places of the results of operations involving division.
    BigNumber.set({ DECIMAL_PLACES: 4 });

    moment.relativeTimeThreshold('s', 60);
    moment.relativeTimeThreshold('m', 60);

    // Each time the connectivity checker gets a response from the /status endpoint, pass that on to the
    // TimeSyncer so we can can check the computer's clock is in sync.
    connectivityChecker.addStatusObserver(
        'timeSyncer',
        (res) => timeSyncer.handleTimeUpdate(res.data.time)
    );

    // Run a connectivity check when the app resumes after losing focus (being minimized or switching tab).
    window.addEventListener(
        'focus',
        () => connectivityChecker.check(),
        {
            passive: true,
        }
    );

    // Emit an API_REQUEST_UNAUTHORIZED event when the (Axios) ApiClient encounters a 401 error.
    apiClient.addErrorHandler(
        (err) => {
            if (err.response && err.response.status && err.response.status === 401) {
                // The response object of both Axios and angular-resource error seem to be compatible.
                $rootScope.$broadcast(EventNames.API_REQUEST_UNAUTHORIZED, err.response);
            }
        }
    );

    // Hide the initial loading overlay.
    let initialLoader = document.getElementById('initial-loader');
    if (initialLoader) {
        initialLoader.parentNode.removeChild(initialLoader);
    }

    // Provide a global function to show/hide a processing message.
    $rootScope.rootProcessingMessage = null;
    /**
     * @param {string|null} [value]
     */
    $rootScope.setRootProcessingMessage = function setRootProcessingMessage(value = null) {
        $timeout(() => {
            $rootScope.rootProcessingMessage = value || null;
        });
    };

    // Add standard date & time formats to templates.
    // These are format strings for moment not for Angular's own date filter.
    // Use this like: {{ myDate | moment:DateFormat.DATE }}
    $rootScope.DateFormat = DateFormat;

    $rootScope.$on(
        EventNames.USER_LOGGED_IN,
        () => {
            setLoggedInBodyClass(true);
        }
    );

    // When a user and device are both set:
    // - Select an order and item branch, if not already selected.
    // - Set the current user on the device in the backend.
    deviceLoginListener.addListener(() => {
        userService.selectAppropriateBranches(currentDevice.device);

        if (currentDevice.getDeviceId()) {
            // It's possible for there to still be no device if we're on the kitchen screen.
            container.deviceManager
                .setCurrentUser(currentDevice.getDeviceId(), userService.getUserID())
                .then(() => {
                })
                .catch(() => {
                });
        }
    });

    // This used to be a directive on the root Angular element. But since it listens for events on $window there
    // is no longer a need for it to be a directive.
    angular.element($window).on('keydown', function (event) {
        $rootScope.$broadcast(EventNames.KEY_DOWN, event);
        if (event.key === 'Escape') {
            $rootScope.$broadcast(EventNames.ESC_KEY_DOWN, event);
        }
    });
};

module.exports = runAppCommon;
